import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton as MuiIconButton,
  Typography,
  useTheme,
} from "@mui/material";
import Masonry from "@mui/lab/Masonry";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import MenuIcon from "@mui/icons-material/Menu";
import CropFreeIcon from "@mui/icons-material/CropFree";
import ReplayIcon from "@mui/icons-material/Replay";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetAwsDownloadUrl } from "../../../../actions/user";
import { useMediaDimensions } from "../../../../hooks/useMediaDimensions";
import {
  BOX_SHADOW_LITTLE,
  PRIMARY_COLOR_TEXT,
} from "../../../../utils/colors";
import { useTranslationApp } from "../../../../lib/i18next";
import IconButton from "../../../Buttons/IconButton";
import { gallerySecondaryToolbarIconSx } from "../../../Gallery/galleryIconButtonStyles";
import {
  MODAL_BATCH_IMAGE_EDIT_IMAGE_ALT,
  MODAL_BATCH_IMAGE_EDIT_RETRY,
  MODAL_BATCH_IMAGE_EDIT_FAILED_BADGE,
  MODAL_IMAGE_EDIT_EDITING_LABEL,
} from "../../../../i18n/keysTranslations";
import { BATCH_ITEM_STATUS } from "../hooks/useBatchImageEdit";
import { getTileDisplayState } from "./tileDisplayState";

import { LaunchError } from "../../../../utils/errors";

const sxActionStrip = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  width: "100%",
  flexShrink: 0,
  px: 1,
  py: 0.75,
  containerType: "inline-size",
  borderTop: "1px solid rgba(0, 0, 0, 0.08)",
  backgroundColor: "#ffffff",
};

const sxGalleryActionClusterStart = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: 0.5,
  minWidth: 0,
  overflow: "visible",
  flexShrink: 1,
};

const sxGalleryActionClusterEnd = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  marginLeft: "auto",
  flexShrink: 0,
};

const BatchImageTile = ({
  itemState,
  visibleVersion,
  isGenerateInProgress,
  isRetryingThisItem,
  onExpand,
  onRetry,
  t,
}) => {
  const theme = useTheme();
  const dispatch = useDispatchApp();
  const [isDownloading, setIsDownloading] = useState(false);

  const { imageUrl, status } = useMemo(
    () => getTileDisplayState(itemState, visibleVersion),
    [itemState, visibleVersion]
  );

  const isPending = status === BATCH_ITEM_STATUS.PENDING || isRetryingThisItem;
  const isError = status === BATCH_ITEM_STATUS.ERROR;
  const showFailedBadge = isError && !isPending;
  const showRetry =
    isError &&
    visibleVersion > 1 &&
    !isGenerateInProgress &&
    !isRetryingThisItem;
  const canDownload = Boolean(imageUrl) && !isPending;
  const canExpand = Boolean(imageUrl) && !isPending;

  const { aspectRatio } = useMediaDimensions(imageUrl || null);
  const containerAspectRatio = aspectRatio || 4 / 5;

  const secondaryToolbarSx = gallerySecondaryToolbarIconSx({
    theme,
    iconSvgFontSize: 16,
  });

  const onDownload = useCallback(
    async (event) => {
      event?.stopPropagation?.();
      if (!imageUrl || isPending) return;

      try {
        setIsDownloading(true);
        await dispatch(startGetAwsDownloadUrl(imageUrl));
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "BatchImageTile",
            capture: "exception",
          })
        );
        console.error("Error downloading batch image:", error);
      } finally {
        setIsDownloading(false);
      }
    },
    [dispatch, imageUrl, isPending]
  );

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: BOX_SHADOW_LITTLE,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: containerAspectRatio,
          flexShrink: 0,
          overflow: "hidden",
          bgcolor: "action.hover",
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={t(MODAL_BATCH_IMAGE_EDIT_IMAGE_ALT)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              opacity: isPending ? 0.5 : 1,
              transition: "opacity 0.3s ease",
            }}
          />
        ) : null}

        {showFailedBadge ? (
          <Chip
            label={t(MODAL_BATCH_IMAGE_EDIT_FAILED_BADGE)}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              height: 24,
              bgcolor: "error.main",
              color: "#ffffff",
              fontWeight: 600,
              "& .MuiChip-label": {
                px: 1,
                color: "#ffffff",
              },
            }}
          />
        ) : null}

        {isPending ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
            }}
          >
            <CircularProgress sx={{ color: "#000000" }} />
            <Typography
              variant="body2"
              sx={{
                color: "#000000",
                fontWeight: 500,
                px: 2,
                textAlign: "center",
              }}
            >
              {t(MODAL_IMAGE_EDIT_EDITING_LABEL)}
            </Typography>
          </Box>
        ) : null}
      </Box>

      {canDownload || canExpand || showRetry ? (
        <Box sx={sxActionStrip} onClick={(event) => event.stopPropagation()}>
          <Box
            sx={{
              ...sxGalleryActionClusterStart,
              ...secondaryToolbarSx,
            }}
          >
            {canDownload ? (
              <IconButton
                onClick={onDownload}
                type="icon"
                loaderColor={PRIMARY_COLOR_TEXT}
                loading={isDownloading}
                edge={false}
              >
                <DownloadIcon
                  sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
                />
              </IconButton>
            ) : null}

            {canExpand ? (
              <IconButton
                onClick={() => onExpand(itemState.selectionId)}
                type="icon"
                edge={false}
              >
                <CropFreeIcon
                  sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
                />
              </IconButton>
            ) : null}
          </Box>

          {showRetry ? (
            <Box
              component="button"
              type="button"
              onClick={() => onRetry(itemState.selectionId)}
              aria-label={t(MODAL_BATCH_IMAGE_EDIT_RETRY)}
              sx={{
                ...sxGalleryActionClusterEnd,
                border: "none",
                background: "none",
                cursor: "pointer",
                color: PRIMARY_COLOR_TEXT,
                p: 0.5,
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              <ReplayIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {t(MODAL_BATCH_IMAGE_EDIT_RETRY)}
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

const BatchImageGrid = ({
  items,
  visibleVersion,
  isGenerateInProgress,
  retryingSelectionIds,
  isSidebarOpen,
  gridStatusAnnouncement,
  onExpand,
  onRetry,
  onToggleSidebar,
  onClose,
  showCloseButton = true,
}) => {
  const { t } = useTranslationApp();

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 2, sm: 4 },
          pt: { xs: showCloseButton ? 6 : 2, sm: showCloseButton ? 6 : 2 },
          pb: { xs: 1, sm: 2 },
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          background: (theme) =>
            `linear-gradient(135deg, ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.03)"
                : "rgba(0,0,0,0.02)"
            } 0%, ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.04)"
            } 100%)`,
        }}
      >
        {!isSidebarOpen ? (
          <MuiIconButton
            onClick={onToggleSidebar}
            sx={{
              position: "absolute",
              top: 15,
              left: 10,
              zIndex: 10,
            }}
          >
            <MenuIcon />
          </MuiIconButton>
        ) : null}

        {showCloseButton ? (
          <MuiIconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 15,
              right: 6,
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </MuiIconButton>
        ) : null}

        <Box
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        >
          {gridStatusAnnouncement}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            py: 1,
          }}
        >
          <Masonry
            columns={{ xs: 2, sm: 3, lg: 4 }}
            spacing={2}
            sx={{ width: "auto", m: 0, alignContent: "flex-start" }}
          >
            {items.map((itemState) => (
              <BatchImageTile
                key={itemState.selectionId}
                itemState={itemState}
                visibleVersion={visibleVersion}
                isGenerateInProgress={isGenerateInProgress}
                isRetryingThisItem={retryingSelectionIds.includes(
                  itemState.selectionId
                )}
                onExpand={onExpand}
                onRetry={onRetry}
                t={t}
              />
            ))}
          </Masonry>
        </Box>
      </Box>
    </Box>
  );
};

export default BatchImageGrid;
